import Category from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import { IExpandedCategoryInfo } from '@Client/runner.nav.categories/components/CategoryNavigation';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import { IFormattedCategory } from '@Client/runner.services/start.flows.formatter';
import FlowModelSummary, {
  IFlowModelSummary
} from '@Client/runner.shared/FlowModelList/FlowModelSummary';
import Search from '@Client/runner.shared/Search';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import {
  IFlowModelDeletedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import CustomTabs, {
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import TagFilter from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import React, { useEffect, useState } from 'react';

type Props = {
  backFromState?: string;
};

const FlowsActive = (props: Props) => {
  const { backFromState } = props;
  const flowsUtilityService = getService('flowsUtilityService');
  const APP_CONFIG = getService('APP_CONFIG');
  const permissionsService = getService('permissionsService');
  const browserUtilsService = getService('browserUtilsService');
  const sessionService = getService('sessionService');
  const $state = getService('$state');
  const flowListManager = getService('flowListManager');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const appInsightsService = getService('appInsightsService');
  const pubsubService = getService('pubsubService');
  const tempModelerUrlService = getService('tempModelerUrlService');

  if (
    !permissionsService.currentUserHasPermission(
      FlowinglyPermissions.FLOW_START
    )
  ) {
    $state.go('app.runner.flowsin');
    return;
  }

  const allCategoriesId = 'all-categories';
  const isMobile = browserUtilsService.isMobileDevice();

  const [categories, setCategories] = useState<
    ITreeCategory<IPublishedFlowModel>[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    backFromState === 'app.runner.flow'
      ? flowListManager.getActiveFlowCategoryId()
      : allCategoriesId
  );
  const [displayedFlowModels, setDisplayedFlowModels] = useState<
    IPublishedFlowModel[]
  >([]);
  const [dataIsReady, setDataIsReady] = useState(false);
  const [lowerCaseSearchText, setLowerCaseSearchText] = useState('');
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const [allFlowModelTagIds, setAllFlowModelTagIds] = useState<Guid[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(FlowsActiveTabs.All);

  const canCreateFlowModels = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.FLOWMODEL_CREATE
  );

  const [emptyPageTitle] = useState<string>(
    'Hi ' + sessionService.getUser().fullName.split(' ')[0]
  );
  const emptyPageText =
    selectedTabIndex === FlowsActiveTabs.Frequent
      ? 'You have not started any workflows matching the current filters. '
      : canCreateFlowModels
      ? `Use ${APP_CONFIG.brandingName} to easily map and automate your repetitive business processes.` +
        '<br /> To start a Flow, you must have at least one Workflow published to you.'
      : 'To start a Flow, you must have at least one Workflow published to you.' +
        '<br />Contact your administrator to have Workflows shared with you.';
  const emptyPageButtonLabel =
    selectedTabIndex === FlowsActiveTabs.Frequent ||
    !canCreateFlowModels ||
    isMobile
      ? ''
      : 'Create and Publish a Workflow';
  const [tabs] = useState<ICustomTab[]>([
    { key: 'all', label: 'All workflows' },
    { key: 'frequent', label: 'Frequently started' }
  ]);

  const refreshFlows = () => {
    return flowListManager
      .getCategorizedWorkflows()
      .then((workflowsByCategory) => {
        addAllFlowsCategory(workflowsByCategory);
        const treeViewCategories =
          runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
            workflowsByCategory
          );
        setCategories(treeViewCategories);
      });
  };

  const addAllFlowsCategory = (
    categories: IFormattedCategory<IPublishedFlowModel>[]
  ) => {
    if (!categories || categories.find((c) => c.id === allCategoriesId)) {
      return;
    }
    const copyOfAllFlows =
      runnerCategoryNavigationService.getFlowModelsFromNestedCategories<IPublishedFlowModel>(
        categories
      );
    categories.unshift({
      name: 'All',
      id: allCategoriesId,
      flowModels: copyOfAllFlows
    });
  };

  const selectCategory = (
    categoryId: string,
    categoryIndex: string,
    categoryName: string,
    expanded: IExpandedCategoryInfo
  ) => {
    const targetCategory =
      runnerCategoryNavigationService.findNodeById<IPublishedFlowModel>(
        categories,
        categoryId
      );
    if (!targetCategory) {
      return;
    }
    if (categoryId != null) {
      flowListManager.setActiveFlowCategoryId(categoryId);
    }

    setSelectedCategoryId(categoryId);
  };

  const updateDisplayFlowModels = () => {
    const selectedCategory =
      runnerCategoryNavigationService.findNodeById<IPublishedFlowModel>(
        categories,
        selectedCategoryId
      );
    if (!selectedCategory?.flowModels) {
      setDisplayedFlowModels([]);
      return;
    }
    const flowModels = selectedCategory.flowModels.filter((flowModel) => {
      const matchesSearchText =
        !lowerCaseSearchText ||
        flowModel.name.toLowerCase().includes(lowerCaseSearchText) ||
        (flowModel.description &&
          flowModel.description.toLowerCase().includes(lowerCaseSearchText));
      const matchesTagFilter =
        tagFilterIds.length === 0 ||
        (flowModel.parsedTags &&
          flowModel.parsedTags.some((tag) => tagFilterIds.includes(tag.id)));
      return matchesSearchText && matchesTagFilter;
    });
    if (selectedTabIndex === FlowsActiveTabs.All) {
      const orderedFlowModels = flowsUtilityService.orderItemsAlphabetically(
        flowModels,
        'name'
      );

      setDisplayedFlowModels(orderedFlowModels);
    } else if (selectedTabIndex === FlowsActiveTabs.Frequent) {
      const orderedFlowModels = flowModels
        .slice()
        .sort((a, b) => b.countOfFlowStarted - a.countOfFlowStarted);
      setDisplayedFlowModels(orderedFlowModels);
    }
  };

  const updateTagFilter = (tagIds: Guid[]) => {
    setTagFilterIds(tagIds);
  };

  const goToFlowStartPage = (flowModelId: Guid) => {
    $state.go(
      STATE_MAPPINGS.flowstart,
      { flowId: EmptyGuid, flowModelId: flowModelId },
      { notify: true }
    );
  };

  const onFlowModelNameChanged = (msg: PubSubEvent, data: string) => {
    const parsedData = JSON.parse(data) as { id: Guid; name: string };
    const flowUpdated = categories.some((category) =>
      category.flowModels.some((flowModel) => {
        if (flowModel.id === parsedData.id) {
          flowModel.name = parsedData.name;
          return true;
        }
        return false;
      })
    );
    if (flowUpdated) {
      setCategories([...categories]);
    }
  };

  useEffect(() => {
    refreshFlows().then(() => {
      appInsightsService.trackMetricIfTimerExist('flowsActiveEntered');
    });

    const activeFlowsSubscriberId = 'flowingly.runner.flows.active';
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      onFlowModelNameChanged,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_SETUP_CATEGORY_DELETED',
      refreshFlows,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe<IFlowModelDeletedMessage>(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      (eventName, message) => {
        const removeFlowModel = (
          categories: ITreeCategory<IPublishedFlowModel>[]
        ) => {
          if (!categories) {
            return;
          }
          for (const category of categories) {
            category.flowModels = category.flowModels.filter(
              (fm) => fm.id !== message.flowModelId
            );
            removeFlowModel(category.items);
          }
        };
        removeFlowModel(categories);

        setCategories([...categories]);
      },
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      refreshFlows,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      refreshFlows,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_ACTOR_DELETED',
      (event, jsonParams: string) => {
        const params = JSON.parse(jsonParams);
        const updatedFlowModelIds = [];
        categories.forEach((category) => {
          category.flowModels.forEach((flowModel) => {
            if (flowModel.processOwnerName === params.actorName) {
              flowModel.processOwnerName = null;
              updatedFlowModelIds.push(flowModel.id);
            }
          });
        });

        if (updatedFlowModelIds.length > 0) {
          setCategories([...categories]);
        }
      },
      activeFlowsSubscriberId
    );

    return () => {
      pubsubService.unsubscribeAll(activeFlowsSubscriberId);
    };
  }, []);

  useEffect(() => {
    if (backFromState === 'app.runner.flow') {
      const activeCategoryId = flowListManager.getActiveFlowCategoryId();
      selectCategory(activeCategoryId, null, null, null);
    } else {
      selectCategory(allCategoriesId, null, null, null);
    }
    const usedTagids = categories.flatMap((c) =>
      c.flowModels.flatMap(
        (flowModel) => flowModel.parsedTags?.map((tag) => tag.id) || []
      )
    );
    setAllFlowModelTagIds(usedTagids);
  }, [categories]);

  useEffect(() => {
    updateDisplayFlowModels();
    if (categories.length > 0) {
      setDataIsReady(true);
    }
  }, [
    categories,
    selectedTabIndex,
    selectedCategoryId,
    lowerCaseSearchText,
    tagFilterIds
  ]);

  return (
    <div id="flows-active">
      <RunnerPageDescription
        showText={!isMobile}
        pageHeaderText="Start Flows"
        pageDescription="A list of workflows published to you."
        helpLink="https://help.flowingly.net/getting-started/how-do-i-start-a-flow"
      />
      {isMobile && categories.length > 0 && (
        <div className="flows-active-content-mobile">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
          />
          {displayedFlowModels.map((flowModel) => {
            const summary: IFlowModelSummary = {
              id: flowModel.id,
              name: flowModel.name,
              description: flowModel.description,
              category: flowModel.category,
              processOwnerName: flowModel.processOwnerName || 'N/A',
              parsedTags: flowModel.parsedTags || []
            };
            return (
              <FlowModelSummary
                key={summary.id}
                isMobile={true}
                flowModelSummary={summary}
                showConfidentialType={true}
                onFlowModelClick={goToFlowStartPage}
              />
            );
          })}
        </div>
      )}
      {!isMobile && dataIsReady && (
        <div className="flows-active-content">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
          />
          <div className="flows-active-list">
            <div className="toolbar">
              <Search
                value={lowerCaseSearchText}
                onValueChange={(newText: string) => {
                  setLowerCaseSearchText(newText.toLowerCase());
                }}
                placeholder="Search by workflow name or description"
              />
              <CustomTabs
                tabs={tabs}
                selectedTabIndex={selectedTabIndex}
                onChange={(e, newValue) => setSelectedTabIndex(newValue)}
              />
              <TagFilter
                usedTagIds={allFlowModelTagIds}
                onApply={updateTagFilter}
              ></TagFilter>
            </div>
            {displayedFlowModels.map((flowModel) => {
              const summary: IFlowModelSummary = {
                id: flowModel.id,
                name: flowModel.name,
                description: flowModel.description,
                category: flowModel.category,
                processOwnerName: flowModel.processOwnerName || 'N/A',
                parsedTags: flowModel.parsedTags || []
              };
              return (
                <FlowModelSummary
                  key={summary.id}
                  isMobile={false}
                  flowModelSummary={summary}
                  showConfidentialType={true}
                  onFlowModelClick={goToFlowStartPage}
                />
              );
            })}
            {(!categories.some((c) => c.id !== allCategoriesId) ||
              displayedFlowModels.length === 0) && (
              <EmptyState
                summaryText={emptyPageTitle}
                bodyText={emptyPageText}
                buttonText={emptyPageButtonLabel}
                onButtonClick={() => tempModelerUrlService.openModeler()}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsActive;

const enum FlowsActiveTabs {
  All = 0,
  Frequent = 1
}
